import {
  TBundle,
  TSession,
} from '@fe-learner-common/api/schemas/sessionsSchema';
import { Duration, TCatalogFilter } from 'types/filter';
import { dateIsAfter, dateIsBefore } from '@fe-learner-common/utils/date';

export type TBundleItem = TBundle & {
  type: 'bundle';
};
export type TSessionItem = TSession & {
  type: 'session';
};
export type TItem = TBundleItem | TSessionItem;

const compareStrings = ({
  searchTerm,
  value,
}: {
  searchTerm: string;
  value?: string | null;
}) => {
  return value && value.toLowerCase().includes(searchTerm.toLowerCase());
};

const isWithinDuration = ({
  duration,
  sessionDuration,
}: {
  duration: Duration;
  sessionDuration: number;
}) => {
  if (!duration) return true;

  const { min, max } = duration;

  return min <= sessionDuration && sessionDuration <= max;
};

const hasAward = ({
  session,
  awards,
}: {
  session: TSession;
  awards: Set<string>;
}) => {
  if (!awards?.size) return true;
  if (!session) return true;

  if (!session.certificatesAvailable && awards.has('certificate')) return false;
  if (!session.diplomasAvailable && awards.has('diploma')) return false;

  return true;
};

const hoursToSeconds = (hours: number) => {
  if (!hours) return 0;
  return hours * 60 * 60;
};

const isValueInSet = (
  value: string | null | undefined,
  set: Set<string> | undefined,
): boolean => {
  return !!(value && set?.size && set.has(value));
};

const applySessionFilter = ({
  filter,
  category,
  session,
}: {
  filter: TCatalogFilter;
  category?: number | null;
  session?: TSession;
}) => {
  if (!session) {
    return false;
  }

  if (
    filter.search &&
    !compareStrings({ searchTerm: filter.search, value: session?.name })
  ) {
    return false;
  }

  if (category && !session.categories?.includes(category)) {
    return false;
  }

  if (
    (filter?.duration?.min || filter?.duration?.max) &&
    !isWithinDuration({
      duration: {
        min: hoursToSeconds(filter.duration.min),
        max: hoursToSeconds(filter.duration.max),
      },
      sessionDuration: session.duration || 0,
    })
  ) {
    return false;
  }

  if (
    filter?.language?.size &&
    !isValueInSet(session?.language, filter?.language)
  ) {
    return false;
  }
  if (filter?.format?.size && !isValueInSet(session?.format, filter?.format)) {
    return false;
  }
  if (
    filter?.location?.size &&
    !isValueInSet(session?.location, filter?.location)
  ) {
    return false;
  }

  if (filter?.award?.size && !hasAward({ session, awards: filter.award })) {
    return false;
  }

  if (
    filter.date?.start &&
    !dateIsAfter(session.startDate, filter.date?.start)
  ) {
    return false;
  }

  if (filter.date?.end && !dateIsBefore(session.endDate, filter.date?.end)) {
    return false;
  }

  return true;
};

// applies OR logic for search and category (bundle OR session)
// AND logic for all other filter criteria
const applyBundleFilter = ({
  filter,
  category,
  bundle,
  sessionMap,
}: {
  filter: TCatalogFilter;
  category?: number | null;
  bundle?: TBundleItem;
  sessionMap?: Map<number, TSession>;
}) => {
  if (!bundle) {
    return false;
  }

  const names = [
    bundle.name,
    ...bundle.sessions.map((id) => sessionMap?.get(id)?.name),
  ];

  if (
    filter.search &&
    !names.some((name) =>
      compareStrings({ searchTerm: filter.search, value: name }),
    )
  ) {
    return false;
  }

  const categories = [
    ...bundle.categories,
    ...bundle.sessions.flatMap((id) => sessionMap?.get(id)?.categories),
  ];

  if (category && !categories?.includes(category)) {
    return false;
  }

  if (!bundle.sessions?.length) {
    return true;
  }

  // since OR logic is applied between bundle and session for search and category (above),
  // don't pass `search` and `category` to session filter
  // otherwise, e.g. if bundle matches but session does not, the bundle won't show
  const sessionFilter = { ...filter, search: '' };
  const sessionMatch = bundle.sessions.some((sessionId) => {
    const session = sessionMap?.get(sessionId);
    return applySessionFilter({ session, filter: sessionFilter });
  });

  if (!sessionMatch) {
    return false;
  }

  return true;
};

export const applyFilterFn = ({
  filter,
  category,
  sessionMap,
}: {
  filter: TCatalogFilter;
  category?: number | null;
  sessionMap?: Map<number, TSession>;
}): ((item: TItem) => boolean) => {
  return (item) => {
    if (item.type === 'session') {
      return applySessionFilter({
        filter,
        category,
        session: item,
      });
    }

    if (item.type === 'bundle') {
      return applyBundleFilter({
        filter,
        category,
        bundle: item,
        sessionMap,
      });
    }

    return false;
  };
};
